import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
import { PageHeader, Card, LoadingSpinner, StatusBadge } from '../components/Layout';
import { api } from '../services/api';
import { Order, OrderStatus } from '../types';

const ACTIVE_STATUSES = 'submitted,pending_release,approved,in_production,ready_to_ship';

const STATUS_FILTERS: { value: string; label: string }[] = [
  { value: '', label: 'All Active' },
  { value: 'submitted', label: 'Submitted' },
  { value: 'pending_release', label: 'Pending Release' },
  { value: 'approved', label: 'Approved' },
  { value: 'in_production', label: 'In Production' },
  { value: 'ready_to_ship', label: 'Ready to Ship (Freight)' },
  { value: 'ready_for_pickup', label: 'Ready for Pickup (Will Call)' },
];

export default function OrdersPage() {
  const [searchParams] = useSearchParams();
  const dealerIdFilter = searchParams.get('dealer') || '';
  const [dealerName, setDealerName] = useState('');
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [selectedOrders, setSelectedOrders] = useState<string[]>([]);
  const [actionLoading, setActionLoading] = useState<string | null>(null);
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  const [allTotal, setAllTotal] = useState(0);

  useEffect(() => {
    if (dealerIdFilter) {
      api.dealers.getDealer(dealerIdFilter).then((d: any) => {
        setDealerName(d.companyName || d.company_name || d.dba_name || '');
      }).catch(() => {});
    }
  }, [dealerIdFilter]);

  // Debounce search input so each keystroke doesn't fire a request.
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearch(searchQuery.trim()), 300);
    return () => clearTimeout(t);
  }, [searchQuery]);

  useEffect(() => {
    loadOrders();
  }, [statusFilter, page, dealerIdFilter, debouncedSearch]);

  // Searching always resets to the first page so we don't sit on a stale page #.
  useEffect(() => {
    if (page !== 1) setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  useEffect(() => {
    loadStatusCounts();
  }, []);

  const mapOrder = (order: any) => ({
    ...order,
    dealerName: order.dealerName || order.companyName || order.company_name || 'Unknown Dealer',
    total: order.total || order.totalAmount || order.total_amount || 0,
    orderNumber: order.orderNumber || order.order_number,
    createdAt: order.createdAt || order.created_at,
    updatedAt: order.updatedAt || order.updated_at,
    depositPaidAt: order.depositPaidAt || order.deposit_paid_at,
    deposit_paid: order.deposit_paid || order.depositPaid,
    credit_approved: order.credit_approved || order.creditApproved,
  });

  const loadStatusCounts = async () => {
    try {
      const data = await api.getOrderStatusCounts();
      setStatusCounts(data.counts || {});
      setAllTotal(data.total || 0);
    } catch (err) {
      console.error('Failed to load status counts:', err);
    }
  };

  const loadOrders = async () => {
    try {
      setLoading(true);
      const response = await api.orders.getOrders({
        status: statusFilter ? statusFilter as OrderStatus : undefined,
        statusIn: statusFilter || dealerIdFilter || debouncedSearch ? undefined : ACTIVE_STATUSES,
        dealerId: dealerIdFilter || undefined,
        includeArchived: !!dealerIdFilter || !!debouncedSearch,
        search: debouncedSearch || undefined,
        page,
        limit: 50,
      } as any);
      const allOrders = (response.data || response.items || []).map(mapOrder);
      setOrders(allOrders);
      setTotalPages(response.totalPages || response.pagination?.totalPages || 1);
      setTotalCount(response.total || response.pagination?.total || allOrders.length);
    } catch (error) {
      console.error('Failed to load orders:', error);
      setOrders([]);
      setTotalPages(1);
    } finally {
      setLoading(false);
    }
  };

  const handleReleaseOrder = async (orderId: string) => {
    try {
      setActionLoading(orderId);
      await api.orders.releaseOrder(orderId);
      await loadOrders();
      await loadStatusCounts();
    } catch (error: any) {
      const msg = error?.message || 'Failed to release order';
      alert(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const handleBulkRelease = async () => {
    try {
      setActionLoading('bulk');
      for (const orderId of selectedOrders) {
        await api.orders.releaseOrder(orderId);
      }
      setSelectedOrders([]);
      await loadOrders();
      await loadStatusCounts();
    } catch (error: any) {
      const msg = error?.message || 'Failed to release orders';
      alert(msg);
    } finally {
      setActionLoading(null);
    }
  };

  const toggleOrderSelection = (orderId: string) => {
    setSelectedOrders(prev =>
      prev.includes(orderId)
        ? prev.filter(id => id !== orderId)
        : [...prev, orderId]
    );
  };

  const toggleAllOrders = () => {
    const releasableOrders = orders.filter(o => o.status === 'pending_release' || o.status === 'approved');
    if (selectedOrders.length === releasableOrders.length) {
      setSelectedOrders([]);
    } else {
      setSelectedOrders(releasableOrders.map(o => o.id));
    }
  };

  // Server already filters on order_number / sidemark / po_number / company_name
  // when `search` is sent, so don't re-narrow here (the old client filter dropped
  // company + po_number matches because it only re-checked orderNumber/dealerName/sidemark).
  const filteredOrders = orders;

  const releasableCount = orders.filter(o => (o.status === 'pending_release' || o.status === 'approved')).length;

  // Active counts
  const activeCount = (statusCounts.submitted || 0) + (statusCounts.pending_release || 0) +
    (statusCounts.approved || 0) + (statusCounts.in_production || 0) + (statusCounts.ready_to_ship || 0) +
    (statusCounts.ready_for_pickup || 0);

  return (
    <div>
      <PageHeader
        title={dealerIdFilter ? `Orders — ${dealerName || 'Dealer'}` : 'Active Orders'}
        subtitle={dealerIdFilter ? `All orders for this dealer` : 'Orders in the production pipeline'}
      />

      {/* Stats Cards */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-8 gap-3 mb-6">
        <Card className="text-center !p-3">
          <p className="text-xl font-bold text-gray-900">{activeCount}</p>
          <p className="text-xs text-gray-500">Active</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-yellow-400">
          <p className="text-xl font-bold text-yellow-600">{statusCounts.submitted || 0}</p>
          <p className="text-xs text-gray-500">Submitted</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-orange-400">
          <p className="text-xl font-bold text-orange-600">{(statusCounts.pending_release || 0) + (statusCounts.approved || 0)}</p>
          <p className="text-xs text-gray-500">Pending / Approved</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-blue-400">
          <p className="text-xl font-bold text-blue-600">{statusCounts.in_production || 0}</p>
          <p className="text-xs text-gray-500">In Production</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-indigo-400">
          <p className="text-xl font-bold text-indigo-600">{statusCounts.ready_to_ship || 0}</p>
          <p className="text-xs text-gray-500">Ready to Ship</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-green-400">
          <p className="text-xl font-bold text-green-600">{statusCounts.ready_for_pickup || 0}</p>
          <p className="text-xs text-gray-500">Ready for Pickup</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-purple-400">
          <p className="text-xl font-bold text-purple-600">{statusCounts.shipped || 0}</p>
          <p className="text-xs text-gray-500">Shipped</p>
        </Card>
        <Card className="text-center !p-3 border-l-4 border-emerald-400">
          <p className="text-xl font-bold text-emerald-600">{allTotal}</p>
          <p className="text-xs text-gray-500">All Orders</p>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="flex-1">
            <input
              type="text"
              placeholder="Search by order #, sidemark, PO #, or dealer..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            />
          </div>
          <div>
            <select
              value={statusFilter}
              onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            >
              {STATUS_FILTERS.map(status => (
                <option key={status.value} value={status.value}>{status.label}</option>
              ))}
            </select>
          </div>
        </div>
      </Card>

      {/* Bulk Actions */}
      {selectedOrders.length > 0 && (
        <div className="mb-4 p-4 bg-blue-50 rounded-lg flex items-center justify-between">
          <span className="text-blue-700">
            {selectedOrders.length} order(s) selected
          </span>
          <button
            onClick={handleBulkRelease}
            disabled={actionLoading === 'bulk'}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
          >
            {actionLoading === 'bulk' ? 'Releasing...' : 'Release to Production'}
          </button>
        </div>
      )}

      <Card>
        {loading ? (
          <div className="flex justify-center py-12">
            <LoadingSpinner size="lg" />
          </div>
        ) : (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {releasableCount > 0 && (
                      <th className="px-4 py-3 text-left">
                        <input
                          type="checkbox"
                          checked={selectedOrders.length === releasableCount && releasableCount > 0}
                          onChange={toggleAllOrders}
                          className="rounded border-gray-300"
                        />
                      </th>
                    )}
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Order</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Dealer</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Sidemark</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Total</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Retail Price</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {filteredOrders.length === 0 && (
                    <tr>
                      <td colSpan={9} className="px-4 py-12 text-center text-gray-500">
                        <div className="flex flex-col items-center">
                          <svg className="w-12 h-12 text-gray-300 mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <p className="text-lg font-medium">No orders found</p>
                          <p className="text-sm mt-1">
                            {statusFilter ? `No orders with status "${statusFilter}"` : 'No active orders in the pipeline'}
                          </p>
                        </div>
                      </td>
                    </tr>
                  )}
                  {filteredOrders.map(order => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      {releasableCount > 0 && (
                        <td className="px-4 py-4">
                          {(order.status === 'pending_release' || order.status === 'approved') && (
                            <input
                              type="checkbox"
                              checked={selectedOrders.includes(order.id)}
                              onChange={() => toggleOrderSelection(order.id)}
                              className="rounded border-gray-300"
                            />
                          )}
                        </td>
                      )}
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          <Link to={`/orders/${order.id}`} className="text-blue-600 hover:text-blue-800 font-medium">
                            {order.orderNumber}
                          </Link>
                          {(order as any).hasAlpha && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-amber-100 text-amber-800 border border-amber-300" title="Alpha motor required — order Alpha motor stock for this order">
                              ALPHA
                            </span>
                          )}
                          {(order as any).hasSomfy && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-purple-100 text-purple-800 border border-purple-300" title="Somfy motor required">
                              SOMFY
                            </span>
                          )}
                          {((order as any).isRush || (order as any).is_rush) && (
                            <span className="px-1.5 py-0.5 text-[10px] font-bold rounded bg-red-600 text-white border border-red-700" title="Rush order — production queue priority + 25% surcharge">
                              RUSH
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">{order.dealerName}</td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">{order.sidemark || '-'}</td>
                      <td className="px-4 py-4 whitespace-nowrap">
                        <div className="flex items-center gap-1.5">
                          {order.status === 'ready_to_ship' && ['will_call', 'willcall'].includes(String((order as any).shippingMethod || '').toLowerCase()) ? (
                            // Will-call orders are picked up, not shipped — show "Ready for Pickup".
                            <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700" title="Will Call — ready for the dealer to pick up">
                              Ready for Pickup
                            </span>
                          ) : (
                            <StatusBadge status={order.status} type="order" />
                          )}
                          {(order as any).shortageHold && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-red-100 text-red-700" title={(order as any).shortageDetails ? JSON.parse(typeof (order as any).shortageDetails === 'string' ? (order as any).shortageDetails : JSON.stringify((order as any).shortageDetails)).map((s: any) => s.description).join(', ') : 'Parts shortage'}>
                              Parts Shortage
                            </span>
                          )}
                          {order.status === 'pending_payment' && !(order as any).stripeSessionId && (
                            <span className="px-1.5 py-0.5 text-[10px] font-medium rounded bg-indigo-100 text-indigo-700">Invoice</span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-900">
                        ${Number(order.total).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        {(order as any).customerSellPrice || (order as any).customer_sell_price ? (
                          <span className="text-green-600 font-medium">
                            ${((order as any).customerSellPrice || (order as any).customer_sell_price).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                          </span>
                        ) : (
                          <span className="text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm text-gray-500">
                        {(() => {
                          const depositPaidAt = (order as any).depositPaidAt;
                          if (depositPaidAt) {
                            return (
                              <span title={`Created: ${format(new Date(order.createdAt), 'MMM d, yyyy')}`}>
                                {format(new Date(depositPaidAt), 'MMM d, yyyy')}
                                <span className="text-xs text-gray-400 ml-1">(paid)</span>
                              </span>
                            );
                          }
                          return format(new Date(order.createdAt), 'MMM d, yyyy');
                        })()}
                      </td>
                      <td className="px-4 py-4 whitespace-nowrap text-sm">
                        <div className="flex items-center space-x-2">
                          <Link to={`/orders/${order.id}`} className="text-gray-600 hover:text-gray-900">View</Link>
                          {(order.status === 'pending_release' || order.status === 'approved') && (
                            <button
                              onClick={() => handleReleaseOrder(order.id)}
                              disabled={actionLoading === order.id}
                              className="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                            >
                              {actionLoading === order.id ? '...' : 'Push to Production'}
                            </button>
                          )}
                          {['in_production'].includes(order.status) && (
                            <Link to={`/orders/${order.id}?edit=schedule`} className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded hover:bg-blue-200">
                              Edit Schedule
                            </Link>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {totalPages > 1 && (
              <div className="flex items-center justify-between px-4 py-3 border-t border-gray-200">
                <button
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                <span className="text-sm text-gray-700">
                  Page {page} of {totalPages} ({totalCount} orders)
                </span>
                <button
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                  className="px-4 py-2 text-sm text-gray-700 bg-white border rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            )}
          </>
        )}
      </Card>
    </div>
  );
}
